import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { CERTIFICATIONS, FAQS } from "@/data"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowRight, CheckCircle2 } from "lucide-react"

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
})

const INCLUDED = [
  "Unlimited API calls",
  "Sandbox environment",
  "Webhook delivery",
  "Real-time analytics",
  "Multi-currency support",
  "Dedicated onboarding",
  "24/7 technical support",
  "SLA guarantee",
  "PCI DSS Level 1 coverage",
  "Fraud scoring engine",
  "Chargeback management",
  "Custom reporting",
]

function PricingPage() {
  useSEO({
    title: "Pricing",
    description:
      "Transparent, competitive pricing with no hidden fees. Enterprise payment processing that scales with your business.",
    keywords: "payment processing pricing, transparent fees, enterprise payments",
    canonical: "https://orchestrate.global/pricing",
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-ink relative overflow-hidden pt-32 pb-20">
        <div
          className="grid-bg pointer-events-none absolute inset-0 opacity-20"
          aria-hidden="true"
        />
        <div
          className="bg-blue/15 pointer-events-none absolute top-1/2 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 text-center lg:px-8">
          <AnimatedSection>
            <SectionLabel label="Pricing" centered className="mb-5" />
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
              Competitive rates.
              <br />
              <em className="text-blue-mid not-italic">No surprises.</em>
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="mx-auto max-w-xl text-xl text-white/50">
              Volume-based pricing built for scale. Contact us for a tailored quote.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing card */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="border-border overflow-hidden rounded-3xl border bg-white shadow-2xl">
              <div className="from-blue to-blue-mid h-1.5 bg-gradient-to-r" aria-hidden="true" />
              <div className="grid lg:grid-cols-2">
                {/* Left */}
                <div className="border-border border-b p-8 lg:border-r lg:border-b-0 lg:p-12">
                  <div className="bg-green-light border-green-DEFAULT/20 mb-6 flex w-fit items-center gap-2 rounded-full border px-3 py-1.5">
                    <span
                      className="bg-green-DEFAULT animate-pulse-dot h-1.5 w-1.5 rounded-full"
                      aria-hidden="true"
                    />
                    <span className="text-green-DEFAULT text-xs font-semibold">
                      Operational · 100% uptime
                    </span>
                  </div>
                  <h2 className="font-display mb-3 text-4xl tracking-tight">Custom Pricing</h2>
                  <p className="text-muted mb-8 leading-relaxed">
                    Interchange-plus pricing based on your volume and business model. We work with
                    you to build a fee structure that makes sense.
                  </p>
                  <div className="mb-8 grid grid-cols-3 gap-3">
                    {[
                      { v: "24/7", l: "Support" },
                      { v: "99.9%", l: "Uptime" },
                      { v: "<200ms", l: "Latency" },
                    ].map(({ v, l }) => (
                      <div
                        key={l}
                        className="bg-surface border-border rounded-xl border p-4 text-center"
                      >
                        <p className="font-display text-blue mb-0.5 text-2xl tracking-tight">{v}</p>
                        <p className="text-muted text-xs">{l}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <Button variant="primary" size="lg" className="flex-1" asChild>
                      <Link to="/contact">
                        Talk to Sales <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/docs">Docs</Link>
                    </Button>
                  </div>
                </div>

                {/* Right: Included */}
                <div className="p-8 lg:p-12">
                  <h3 className="text-muted mb-6 text-sm font-semibold tracking-widest uppercase">
                    Everything included
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {INCLUDED.map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <CheckCircle2 className="text-green-DEFAULT h-4 w-4 flex-shrink-0" />
                        <span className="text-ink text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Certifications */}
      <section className="border-border border-y bg-white py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-muted mb-8 text-center text-xs font-semibold tracking-widest uppercase">
            Compliance &amp; certifications
          </p>
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            {CERTIFICATIONS.map(({ label, sub }) => (
              <div
                key={label}
                className="bg-surface border-border rounded-xl border p-4 text-center"
              >
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-muted mt-0.5 text-xs">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-14 text-center">
              <SectionLabel label="FAQ" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">Common questions</h2>
            </div>
          </AnimatedSection>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <AnimatedSection key={faq.id} delay={i * 0.05}>
                <div className="bg-surface border-border rounded-2xl border p-6">
                  <h3 className="mb-2 text-base font-semibold">{faq.question}</h3>
                  <p className="text-muted text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}
